import { Box, Link, Paper, PaperProps, Typography } from '@mui/material';
import React, { createContext, useContext } from 'react';

export interface ListboxToolbarValue {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  accentColor: string;
}

// React context lets the (stable) PaperComponent below read the live
// selection state without recreating the component on every render, which
// would unmount/remount the popper and break in-flight typing/scroll.
export const ListboxToolbarContext = createContext<ListboxToolbarValue | null>(null);

const ListboxToolbar: React.FC<ListboxToolbarValue> = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onClear,
  accentColor,
}) => {
  const allSelected = selectedCount === totalCount && totalCount > 0;
  const noneSelected = selectedCount === 0;
  return (
    <Box
      // Prevent the click from blurring the input and closing the popper
      // before our handler runs.
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {selectedCount} of {totalCount} selected
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Link
          component="button"
          variant="caption"
          type="button"
          onClick={onSelectAll}
          disabled={allSelected}
          aria-label="Select all options"
          sx={{
            color: accentColor,
            cursor: allSelected ? 'default' : 'pointer',
            opacity: allSelected ? 0.5 : 1,
          }}
        >
          Select all
        </Link>
        <Link
          component="button"
          variant="caption"
          type="button"
          onClick={onClear}
          disabled={noneSelected}
          aria-label="Clear selected options"
          sx={{
            color: accentColor,
            cursor: noneSelected ? 'default' : 'pointer',
            opacity: noneSelected ? 0.5 : 1,
          }}
        >
          Clear
        </Link>
      </Box>
    </Box>
  );
};

export const ToolbarPaper = React.forwardRef<HTMLDivElement, PaperProps>(
  function ToolbarPaper(paperProps, ref) {
    const ctx = useContext(ListboxToolbarContext);
    return (
      <Paper {...paperProps} ref={ref}>
        {ctx && <ListboxToolbar {...ctx} />}
        {paperProps.children}
      </Paper>
    );
  }
);
